import { Button } from '@components/atoms/Button/Button';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { ArticleOverviewHeader } from '@components/molecules/ArticleOverviewHeader/ArticleOverviewHeader';
import { ArticleTeaserCard } from '@components/organisms/ArticleTeaserCard/ArticleTeaserCard';
import { usePaginatedQuery } from '@lib/graphql/urql';
import { GetAllArticlesPaginatedDocument } from '@src/lib/graphql/generated';
import {
  AditionInitialization,
  AditionTagPlacement,
  AditionTagWithFallback,
} from '@vgn-medien-holding/vgn-fe-components';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useState } from 'react';
import { RESSORT_ARTICLES_PER_LOAD } from 'tvm-config';

export function Ressort({ data }) {
  const [activeRessort, setActiveRessort] = useState(data.activeRessort ?? null);
  const router = useRouter();

  useEffect(() => {
    const slug = router.asPath?.split('/')?.[1];
    const ressort = data?.ressorts.find((e) => e.slug === slug);

    if (!ressort) return;

    setActiveRessort(ressort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, data?.ressorts]);

  const [
    { fetching: articlesLoading, data: articlesData, hasMorePages: hasMoreArticles, fetchNextPage: fetchMoreArticles },
  ] = usePaginatedQuery({
    query: GetAllArticlesPaginatedDocument,
    variables: {
      portal: 'tvmedia',
      first: RESSORT_ARTICLES_PER_LOAD,
      page: 1,
      orderBy: 'updated_at',
      orderDirection: 'DESC',
      ...(activeRessort?.id && { ressort: activeRessort?.id }),
    },
    requestPolicy: 'cache-and-network',
  });
  const articlesPaginated = articlesData?.flatMap((data) => data?.articlesPaginated?.data);

  const oewa = data?.oewa;
  return (
    <>
      <AditionInitialization data={{ ad_settings: data.ad_settings }} />
      <Oewa
        pageKey={oewa?.category_key || 'RedCont'}
        categoryKey={oewa?.page_key || 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || 'FilmUndKino'}
      />
      <Head>{Meta(activeRessort?.metadata ?? {}, activeRessort?.title ?? 'Magazin')}</Head>
      <AditionTagPlacement
        tag={data.ad_settings?.mobile_middle1}
        classProps={{ root: 'lg:hidden grid place-items-center py-5' }}
      />

      <div className="px-4 py-5 pt-10 pb-28 lg:px-16">
        <ArticleOverviewHeader ressorts={data.ressorts} activeItem={activeRessort} />
        <div className={`relative grid gap-4 md:grid-cols-2 lg:grid-cols-fill-cards-default`}>
          {articlesPaginated?.map((article, idx) => (
            <Fragment key={article?.id}>
              {idx > 1 && idx % 6 === 0 && (
                <div className="grid place-items-center" key={`ad-${idx}`}>
                  <AditionTagWithFallback
                    tag={data.ad_settings?.cad1}
                    fallback={data.ad_settings?.mobile_middle1}
                    breakpoint={'sm'}
                    tagCount={idx / 6 - 1}
                  />
                </div>
              )}
              <ArticleTeaserCard article={article} classProps={{ Image: 'aspect-movie' }} />
            </Fragment>
          ))}
        </div>
        <div className="grid justify-center py-4">
          {hasMoreArticles && (
            <Button
              title="Mehr laden"
              hasAction
              onAction={() => setTimeout(fetchMoreArticles)}
              isLoading={articlesLoading}
              buttonStyle="tertiary"
              classProps={{ root: 'border border-gray-650/25 mt-6' }}
            />
          )}
        </div>
      </div>
    </>
  );
}
